/**
 * Desktop vertical filter sidebar collapse for collection/search PLPs.
 * Initial state is applied by a blocking inline script in filters.liquid (anti-CLS).
 * Layout CSS keys off filter-collapse-component[data-collapsed] (same source as the toggle label).
 */
class FilterCollapseComponent extends HTMLElement {
  static #STORAGE_PREFIX = 'facets-sidebar-collapsed';
  static #FILTER_RENDERED_EVENT = 'filter:rendered';

  /** @type {HTMLElement | null} */
  #pane = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | null} */
  #initFrame = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();

    if (this.#initFrame != null) {
      cancelAnimationFrame(this.#initFrame);
      this.#initFrame = null;
    }

    // Click delegation survives section morph replacing the inner button node.
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
    document.addEventListener(
      FilterCollapseComponent.#FILTER_RENDERED_EVENT,
      /** @type {EventListener} */ (this.#onSectionRendered),
      { signal: this.#controller.signal }
    );

    // The pane is rendered after this component in the DOM. On first parse / section morph,
    // connectedCallback can run before the pane exists — defer until it does.
    this.#initFrame = requestAnimationFrame(() => {
      this.#initFrame = null;
      this.#init();
    });
  }

  disconnectedCallback() {
    if (this.#initFrame != null) {
      cancelAnimationFrame(this.#initFrame);
      this.#initFrame = null;
    }

    this.#controller?.abort();
    this.#controller = null;
  }

  #init() {
    const paneId = this.dataset.paneId;
    if (!paneId) return;

    this.#pane = document.getElementById(paneId);
    if (!this.#pane) {
      // One more frame for slow morphs
      this.#initFrame = requestAnimationFrame(() => {
        this.#initFrame = null;
        this.#pane = document.getElementById(paneId);
        if (!this.#pane) return;
        this.#syncFromStorage();
      });
      return;
    }

    this.#syncFromStorage();
  }

  /**
   * Re-resolve pane refs and apply sessionStorage / merchant default after morph.
   * Inline scripts in morphed HTML do not re-execute, so JS must re-sync here.
   */
  #syncFromStorage() {
    const paneId = this.dataset.paneId;
    if (!paneId) return;

    this.#pane = document.getElementById(paneId);
    if (!this.#pane) return;

    const collapsed = this.#resolveInitialCollapsed();
    this.#applyCollapsed(collapsed, { persist: false });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[ref="toggleButton"]')) return;

    const nextCollapsed = !this.#pane?.classList.contains('hidden');
    this.#applyCollapsed(nextCollapsed, { persist: true });
  };

  /**
   * @param {Event} event
   */
  #onSectionRendered = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const sectionId = event.detail?.sectionId;
    if (sectionId && this.dataset.storageKey && sectionId !== this.dataset.storageKey) return;

    requestAnimationFrame(() => {
      this.#syncFromStorage();
    });
  };

  /**
   * @returns {boolean}
   */
  #resolveInitialCollapsed() {
    const storageKey = this.#storageKey();

    // sessionStorage wins — restores user choice after section re-renders (scripts may not re-run)
    if (!this.#isThemeEditor()) {
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored === 'true') return true;
        if (stored === 'false') return false;
      } catch {
        // sessionStorage unavailable — fall through
      }
    }

    if (this.dataset.defaultCollapsed === 'true') return true;
    if (this.dataset.defaultCollapsed === 'false') return false;

    const htmlState = document.documentElement.dataset.facetsSidebar;
    if (htmlState === 'collapsed') return true;
    if (htmlState === 'expanded') return false;

    return this.dataset.defaultCollapsed === 'true';
  }

  /**
   * @returns {string}
   */
  #storageKey() {
    const sectionKey = this.dataset.storageKey || 'default';
    const defaultPart = this.dataset.defaultCollapsed === 'true' ? 'collapsed' : 'expanded';
    // Merchant default is part of the key so Theme Editor changes aren't blocked by old session values
    return `${FilterCollapseComponent.#STORAGE_PREFIX}:${sectionKey}:${defaultPart}`;
  }

  /**
   * @param {boolean} collapsed
   * @param {{ persist?: boolean }} [options]
   */
  #applyCollapsed(collapsed, { persist = true } = {}) {
    if (!this.#pane) return;

    const toggleButton = this.querySelector('[ref="toggleButton"]');

    this.#pane.classList.toggle('hidden', collapsed);
    this.dataset.collapsed = collapsed ? 'true' : 'false';
    toggleButton?.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    document.documentElement.dataset.facetsSidebar = collapsed ? 'collapsed' : 'expanded';

    const wrapper = this.closest('.collection-wrapper');
    if (wrapper) {
      wrapper.classList.toggle('collection-wrapper--filters-collapsed', collapsed);
      wrapper.classList.toggle('collection-wrapper--filters-expanded', !collapsed);
    }

    if (!persist || this.#isThemeEditor()) return;

    try {
      sessionStorage.setItem(this.#storageKey(), String(collapsed));
    } catch {
      // Ignore quota / private mode errors
    }
  }

  /**
   * @returns {boolean}
   */
  #isThemeEditor() {
    return Boolean(window.Shopify?.designMode);
  }
}

if (!customElements.get('filter-collapse-component')) {
  customElements.define('filter-collapse-component', FilterCollapseComponent);
}
